import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "input",
    "activeLabel",
    "display",
    "cashField",
    "cashFieldSubtotal",
    "cashCard",
    "bankTransfer",
    "expectedCash",
    "countedCash",
    "difference",
    "expectedCashInput",
    "countedCashInput",
    "differenceInput"
  ]

  static values = {
    openingCash: Number,
    cashSales: Number,
    cashRefund: Number
  }

  connect() {
    this.activeInput = null

    if (this.hasInputTarget) {
      this.setActiveInput(this.inputTargets[0])
    }

    // DB에서 불러온 값이 있으면 소계 초기 렌더링
    if (this.hasCashFieldTarget) {
      this.cashFieldTargets.forEach((input, i) => {
        const qty = this.toNumber(input.value)
        if (qty > 0 && this.cashFieldSubtotalTargets[i]) {
          const amount = this.toNumber(input.dataset.amount)
          this.cashFieldSubtotalTargets[i].textContent = this.formatNumber(qty * amount)
        }
      })
    }

    this.updateSummary()
  }

  select(event) {
    this.setActiveInput(event.currentTarget)
  }

  append(event) {
    if (!this.activeInput) return

    const value = event.currentTarget.dataset.value
    const current = this.normalize(this.activeInput.value)
    const next = current === "0" ? value : current + value

    if (next.length > 8) return

    this.activeInput.value = next
    this.updateSummary()
  }

  delete() {
    if (!this.activeInput) return

    const current = this.normalize(this.activeInput.value)
    this.activeInput.value = current.length <= 1 ? "0" : current.slice(0, -1)
    this.updateSummary()
  }

  clear() {
    if (!this.activeInput) return

    this.activeInput.value = "0"
    this.updateSummary()
  }

  setActiveInput(input) {
    this.activeInput = input

    if (this.hasActiveLabelTarget) {
      this.activeLabelTarget.textContent = input.dataset.label || input.name
    }

    if (this.hasCashCardTarget) {
      this.cashCardTargets.forEach(card => card.classList.remove("cash-card-active"))
      const card = input.closest("[data-cash-keypad-target~='cashCard']")
      if (card) card.classList.add("cash-card-active")
    }

    this.updateDisplay()
  }

  updateSummary() {
    this.updateDisplay()

    // 아래는 close 화면처럼 권종/정산 정보가 있는 경우에만 계산
    if (!this.hasCashFieldTarget) return

    const countedCash = this.cashFieldTargets.reduce((sum, input, i) => {
      const qty = this.toNumber(input.value)
      const amount = this.toNumber(input.dataset.amount)
      const subtotal = qty * amount
      if (this.cashFieldSubtotalTargets[i]) {
        this.cashFieldSubtotalTargets[i].textContent = this.formatNumber(subtotal)
      }
      return sum + subtotal
    }, 0)

    const bankTransfer = this.hasBankTransferTarget
        ? this.toNumber(this.bankTransferTarget.value)
        : 0

    const expectedCash =
        this.openingCashValue +
        this.cashSalesValue -
        bankTransfer

    const difference = countedCash - expectedCash

    if (this.hasExpectedCashTarget) {
      this.expectedCashTarget.textContent = `${this.formatNumber(expectedCash)} 원`
    }

    if (this.hasCountedCashTarget) {
      this.countedCashTarget.textContent = `${this.formatNumber(countedCash)} 원`
    }

    if (this.hasDifferenceTarget) {
      const sign = difference > 0 ? "+" : ""
      this.differenceTarget.textContent = `${sign}${this.formatNumber(difference)}원`
    }

    if (this.hasExpectedCashInputTarget) {
      this.expectedCashInputTarget.value = expectedCash
    }

    if (this.hasCountedCashInputTarget) {
      this.countedCashInputTarget.value = countedCash
    }

    if (this.hasDifferenceInputTarget) {
      this.differenceInputTarget.value = difference
    }
  }

  updateDisplay() {
    if (!this.activeInput || !this.hasDisplayTarget) return
    this.displayTarget.textContent = this.formatNumber(this.toNumber(this.activeInput.value))
  }

  normalize(value) {
    if (!value || value === "") return "0"

    const parsed = parseInt(value, 10)
    return Number.isNaN(parsed) ? "0" : String(parsed)
  }

  toNumber(value) {
    const parsed = parseInt(value, 10)
    return Number.isNaN(parsed) ? 0 : parsed
  }

  formatNumber(value) {
    return Number(value).toLocaleString("ko-KR")
  }
}
